import {
  Button,
  IconButton,
  Heading,
  HStack,
  SimpleGrid,
  Stack,
  Text,
  Image,
  ButtonGroup,
  useBreakpointValue,
  Box,
  Modal,
  ModalContent,
  ModalOverlay,
  Textarea,
  ModalFooter,
  ModalBody,
  ModalHeader,
  Input,
  Switch,
  Spinner,
  VStack,
  useToast,
  Progress,
  Select,
  Icon,
  InputGroup,
  InputRightAddon,
  InputRightElement,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  keyframes,
  Checkbox,
  Divider,
  useClipboard,
  FormErrorIcon,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { FiCopy, FiEdit2, FiExternalLink, FiPlus } from "react-icons/fi";

import {
  FaCloudDownloadAlt,
  FaCloudUploadAlt,
  FaPlus,
  FaCheckCircle,
  FaQrcode,
  FaWarehouse,
  FaTrashAlt,
} from "react-icons/fa";

import { TicketTypesTable } from "./TicketTypesTable";
import { PromoCodesTable } from "./PromoCodesTable";
import { AffiliatesTable } from "./AffiliatesTable";

import actions from "../actions";
import {
  CreateAffiliatesRequest,
  CreatePromoCodeRequest,
  KYDAdminEvent,
  KYDAffiliate,
  KYDEventMetrics,
  UpdateAffiliateRequest,
  UpdatePromoCodeRequest,
} from "@common/interfaces/KYDEvent";
import { useParams } from "react-router-dom";
import {
  AdminTicketType,
  CreateTicketTypeRequest,
  KYDTicketTypeTierRequest,
} from "@common/interfaces/KYDTicket";
import ReactS3Uploader from "react-s3-uploader";
import { KYDPromoCode } from "@common/interfaces/KYDCart";

import {
  ModalType,
  getRandomString,
  TieredTicketTypes,
  EditModalType,
  showSuccessToast,
} from "../utils";
import "react-datepicker/dist/react-datepicker.css";
import { TicketSales } from "./EventSalesChart";
import { MarketingTable } from "./MarketingTable";
import { CampaignsTable } from "./CampaignsTable";
import md5 from "md5";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import EditTieredTypeModal from "./EditTieredTypeModal";

import CurrencyInput from "react-currency-input-field";
import { useDialog } from "../Common/Dialog";
import TimelineSection from "./TimelinesSection";
import CodeSection from "./CodeSection";
import { useLoading, useProgress } from "./hooks/form";

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

interface Props {
  label: string;
  value: string;
  height?: string;
}

const App = ({
  setAppModalType,
}: {
  setAppModalType: (state: ModalType, description: string) => void;
}) => {
  const [kydEvent, setKydEvent] = useState<KYDAdminEvent | null>(null);
  const { event_id } = useParams();
  const headerSize = useBreakpointValue({ base: "sm", md: "md" });
  const [modalType, setModalType] = useState(EditModalType.INACTIVE);
  const [dirtyText, setDirtyText] = useState<any>({});
  const [error, setError] = useState({ code: "" });
  const [metrics, setMetrics] = useState<KYDEventMetrics | null>(null);
  const [loading, updateLoading] = useLoading({
    tickettype: false,
    promocode: false,
    create_affiliates: false,
    blocking: false,
    event: false,
    metrics: false,
  });
  const [progress, updateProgress] = useProgress({
    poster: false,
  });

  const { onCopy } = useClipboard(kydEvent?.preview_url);
  const toast = useToast();
  const dialog = useDialog();
  const uploadRef = useRef<HTMLInputElement>(null);

  const [editingTicketType, setEditingTicketType] =
    useState<AdminTicketType | null>(null);
  const [editingPromoCode, setEditingPromoCode] = useState<KYDPromoCode | null>(
    null
  );
  const [editingAffiliate, setEditingAffiliate] = useState<KYDAffiliate | null>(
    null
  );
  const [deleteAffiliate, setDeleteAffiliate] = useState<KYDAffiliate | null>(
    null
  );

  const updateDirtyText = useCallback((update: any | string) => {
    if (update === "all") {
      setDirtyText({});
    } else {
      setDirtyText((l: any) => {
        const keys = Object.keys(update);
        keys.forEach((key) => {
          if (update[key] === null) {
            delete l[key];
          } else {
            l[key] = update[key];
          }
        });
        return { ...l };
      });
    }
  }, []);

  const tiers: TieredTicketTypes | {} = useMemo(() => {
    if (kydEvent) {
      const _tiers: TieredTicketTypes = {};

      const findNext = (next_tier_id: string) => {
        const next_tier = kydEvent.tiered_ticket_types.find(
          (tt) => tt.id === next_tier_id
        );
        return next_tier;
      };

      for (const ticket_type of kydEvent.ticket_types) {
        if (ticket_type.tier === 0) {
          _tiers[ticket_type.id] = [];
          let next_tier_id: string | undefined = ticket_type.next_tier_id;
          if (next_tier_id) {
            do {
              const next_tier = findNext(next_tier_id);
              if (next_tier) {
                _tiers[ticket_type.id].push(next_tier);
                next_tier_id = next_tier?.next_tier_id;
              } else {
                next_tier_id = undefined;
              }
            } while (next_tier_id);
          }
        }
      }

      return _tiers;
    }
  }, [kydEvent]);

  const fetchEvent = useCallback(
    async (eventId?: string, options?: any) => {
      if (!options || (options && !options.silent)) {
        updateLoading("init", true);
      }

      let _;
      try {
        if (!eventId) {
          throw Error("NO event ID provided");
        }
        _ = await actions.ADMIN.fetchEvent(eventId);
        setKydEvent(_);
        dayjs.tz.setDefault(_.timezone);

        if (
          _ &&
          _.latest_update &&
          _.latest_update.status === "PENDING" &&
          !options
        ) {
          updateLoading("init", false);
          updateLoading("blocking", true);
          await pollForEventUpdateStatus();
          updateLoading("blocking", false);
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("init", false);
      return _;
    },
    [updateLoading]
  );

  useEffect(() => {
    const fetchMetrics = async (event_id: string) => {
      updateLoading("metrics", true);
      try {
        const _ = await actions.ADMIN.fetchEventMetrics(event_id, {
          end: kydEvent?.end_at,
        });
        setMetrics(_);
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("metrics", false);
    };
    if (kydEvent) {
      fetchMetrics(kydEvent.id);
    }
  }, [kydEvent, updateLoading, dialog]);

  const onCreatePromoCode = useCallback(
    async (eventId?: string) => {
      let _;
      updateLoading("promocode", true);
      try {
        if (!eventId) {
          throw Error("NO event ID provided");
        }

        console.log("Dirty: ", dirtyText);
        const ticket_type_ids = Object.keys(dirtyText)
          .filter(
            (key) => key.startsWith("promocode.TT") && dirtyText[key] === "true"
          )
          .map((key) => key.split(".").pop() || "");
        const request: CreatePromoCodeRequest = {
          code: dirtyText["promocode.code"],
          amount: dirtyText["promocode.amount"],
          ticket_type_ids: ticket_type_ids,
          mode: dirtyText["promocode.mode"] || "percent",
          enabled: dirtyText["promocode.enabled"] === "true",
          max_total_uses: dirtyText["promocode.max_total_uses"],
          max_uses_per_user: dirtyText["promocode.max_uses_per_user"],
        };

        _ = await actions.ADMIN.createPromoCode(eventId, request);
        await fetchEvent(eventId, { silent: true });
        setModalType(EditModalType.INACTIVE);
        updateDirtyText("all");
        toast({
          title: "Promo Code Created",
          description: "Promo code has been created",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("promocode", false);
      return _;
    },
    [updateLoading, dirtyText, fetchEvent, dialog]
  );

  const onCreateAffiliates = useCallback(
    async (eventId?: string) => {
      let _;
      updateLoading("create_affiliates", true);
      try {
        if (!eventId) {
          throw Error("NO event ID provided");
        }

        console.log("Dirty: ", dirtyText);

        const request: CreateAffiliatesRequest = {
          quantity: dirtyText["create_affiliates.quantity"],
        };

        _ = await actions.ADMIN.createAffiliates(eventId, request);
        await fetchEvent(eventId, { silent: true });
        setModalType(EditModalType.INACTIVE);
        updateDirtyText("all");
        toast({
          title: `${request.quantity} Affiliates Created`,
          description: "Affiliates have been created",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("create_affiliates", false);
      return _;
    },
    [updateLoading, dirtyText, fetchEvent]
  );

  const onUpdatePromoCode = useCallback(
    async (eventId: string, promoCode: KYDPromoCode) => {
      let _;
      updateLoading("promocode", true);
      try {
        console.log("Dirty: ", dirtyText);
        const ticket_type_id_updates = Object.keys(dirtyText)
          .filter((key) => key.startsWith("promocode.TT"))
          .map((key) => key.split(".").pop() || "");

        let ticket_type_ids = promoCode.ticket_type_ids;
        for (const tt_id_update of ticket_type_id_updates) {
          const enabled = dirtyText[`promocode.${tt_id_update}`] === "true";
          if (ticket_type_ids.includes(tt_id_update) && !enabled) {
            ticket_type_ids = ticket_type_ids.filter(
              (id) => id !== tt_id_update
            );
          } else if (!ticket_type_ids.includes(tt_id_update) && enabled) {
            ticket_type_ids.push(tt_id_update);
          }
        }

        const request: UpdatePromoCodeRequest = {
          code: dirtyText["promocode.code"],
          amount: dirtyText["promocode.amount"],
          ticket_type_ids: ticket_type_ids,
          mode: dirtyText["promocode.mode"],
          enabled:
            "promocode.enabled" in dirtyText
              ? dirtyText["promocode.enabled"] === "true"
              : undefined,
          max_total_uses: dirtyText["promocode.max_total_uses"],
          max_uses_per_user: dirtyText["promocode.max_uses_per_user"],
        };

        _ = await actions.ADMIN.updatePromoCode(eventId, promoCode.id, request);
        await fetchEvent(eventId, { silent: true });
        setModalType(EditModalType.INACTIVE);
        updateDirtyText("all");
        toast({
          title: "Promo Code Updated",
          description: "Promo code has been updated",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("promocode", false);
      return _;
    },
    [updateLoading, dirtyText, updateDirtyText, fetchEvent, dialog]
  );

  const onEditAffiliate = useCallback(
    async (
      eventId: string,
      affiliate: KYDAffiliate,
      mode: "edit" | "delete"
    ) => {
      let _;
      updateLoading("edit_affiliate", true);
      try {
        const request: UpdateAffiliateRequest = {
          name: mode === "edit" ? dirtyText["affiliate.name"] : undefined,
          status: mode === "delete" ? "DELETED" : undefined,
        };

        _ = await actions.ADMIN.updateAffiliate(eventId, affiliate.id, request);
        await fetchEvent(eventId, { silent: true });
        setModalType(EditModalType.INACTIVE);
        setEditingAffiliate(null);
        setDeleteAffiliate(null);
        updateDirtyText("all");
        if (mode === "edit") {
          toast({
            title: "Affiliate Updated",
            description: "Affiliate has been updated",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        } else if (mode === "delete") {
          toast({
            title: "Affiliate Deleted",
            description: "Affiliate has been deleted",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("edit_affiliate", false);
      return _;
    },
    [updateLoading, dirtyText, updateDirtyText, fetchEvent, dialog]
  );

  useEffect(() => {
    if (event_id) {
      fetchEvent(event_id);
    }
  }, [event_id, fetchEvent]);

  const pollForEventUpdateStatus = useCallback(async () => {
    console.log("Poll Event Status");
    const searchParams = new URLSearchParams(window.location.search);
    const updateId = searchParams.get("update_id");
    const sleep = (milliseconds: number) => {
      return new Promise((resolve) => setTimeout(resolve, milliseconds));
    };

    if (updateId) {
      let attempts = 0;
      let didFind = false;

      do {
        console.log("Attempt: ", attempts);
        let serverEventUpdate: any;
        let failed = false;
        try {
          serverEventUpdate = await actions.ADMIN.fetchEventUpdate(
            event_id!,
            updateId
          );
        } catch (err) {
          console.log(err);
          failed = true;
        }

        if (
          !failed &&
          (!serverEventUpdate || serverEventUpdate.status === "COMPLETE")
        ) {
          didFind = true;
          searchParams.delete("update_id");

          const newUrl =
            window.location.protocol +
            "//" +
            window.location.host +
            window.location.pathname +
            "?" +
            searchParams.toString();
          window.history.pushState({ path: newUrl }, "", newUrl);

          if (
            serverEventUpdate &&
            serverEventUpdate.status === "COMPLETE" &&
            serverEventUpdate.error
          ) {
            console.log(
              "Found Update: ",
              JSON.stringify(serverEventUpdate, null, 2)
            );
            dialog({
              text: serverEventUpdate.error.message || serverEventUpdate.error,
            });
            return false;
          } else {
            await fetchEvent(event_id!, { silent: true });
          }
          break;
        } else {
          await sleep(3000);
          attempts += 1;
        }
      } while (attempts < 15);

      return didFind;
    }

    return false;
  }, [event_id, fetchEvent]);

  const onUpdateEvent = useCallback(
    async (key: string, override_value?: string) => {
      updateLoading("event", true);
      try {
        const value = override_value || (dirtyText[key] as string);
        const result = await actions.ADMIN.updateEvent(event_id!, key, value);
        console.log("Result: ", result);
        if (result && result.update_id) {
          const params = new URLSearchParams(window.location.search);
          params.set("update_id", result.update_id);

          const newUrl =
            window.location.protocol +
            "//" +
            window.location.host +
            window.location.pathname +
            "?" +
            params.toString();
          window.history.pushState({ path: newUrl }, "", newUrl);

          const success = await pollForEventUpdateStatus();
          if (success) {
            console.log("Updated Event");
            updateDirtyText("all");
            toast({
              title: "Updated",
              description: "Event has been updated",
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          }
        } else {
          await fetchEvent(event_id!, { silent: true });
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("event", false);
      updateProgress("poster", 0);
      setModalType(EditModalType.INACTIVE);
    },
    [
      event_id,
      dirtyText,
      fetchEvent,
      updateLoading,
      pollForEventUpdateStatus,
      updateDirtyText,
      updateProgress,
      dialog,
      toast,
    ]
  );

  const onUpdateTicketType = useCallback(
    async (ticket_type_id: string) => {
      updateLoading("tickettype", true);
      try {
        const ticketType = kydEvent?.ticket_types.find(
          (tt) => tt.id === ticket_type_id
        );
        if (!ticketType) {
          throw Error("No ticket type found");
        }

        const keys = Object.keys(dirtyText).filter((key) => {
          const parts = key.split(".");
          return parts.length === 2 && parts[0] === ticket_type_id;
        });

        const request: any = {};
        keys.forEach((k) => {
          const key = k.split(".")[1];
          const value = dirtyText[k];

          if (
            key !== "quantity" ||
            (key === "quantity" &&
              parseInt(value) >= ticketType.quantity - ticketType.remaining)
          ) {
            request[key] = value;
          }
        });

        const result = await actions.ADMIN.updateTicketType(
          event_id!,
          ticket_type_id,
          request
        );

        if (result && result.update_id) {
          const params = new URLSearchParams(window.location.search);
          params.set("update_id", result.update_id);

          const newUrl =
            window.location.protocol +
            "//" +
            window.location.host +
            window.location.pathname +
            "?" +
            params.toString();
          window.history.pushState({ path: newUrl }, "", newUrl);

          const success = await pollForEventUpdateStatus();
          if (success) {
            toast({
              title: "Updated",
              description: "Ticket type has been updated",
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          }
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("tickettype", false);
      setEditingTicketType(null);
      setModalType(EditModalType.INACTIVE);
      updateDirtyText("all");
    },
    [event_id, dirtyText, fetchEvent, updateDirtyText, updateLoading]
  );

  const onUpdateTieredTicketType = useCallback(
    async (ticket_type_id: string) => {
      updateLoading("tickettype", true);
      try {
        const ticketType = kydEvent?.ticket_types.find(
          (tt) => tt.id === ticket_type_id
        );
        if (!ticketType) {
          throw Error("No ticket type found");
        }

        const on_sale_at = dirtyText["tickettype.on_sale_at"];
        const off_sale_at = dirtyText["tickettype.off_sale_at"];
        const start_at = dirtyText["tickettype.start_at"];
        const end_at = dirtyText["tickettype.end_at"];
        const code = dirtyText["tickettype.code"];
        const description = dirtyText["tickettype.description"];
        const tt_tiers = [ticketType, ...tiers[ticket_type_id]];

        const request: any = {
          ticket_types: [],
          on_sale_at,
          off_sale_at,
          start_at,
          end_at,
          code,
          description,
        };
        for (const tier of tt_tiers) {
          const keys = Object.keys(dirtyText).filter((key) => {
            const parts = key.split(".");
            return parts.length === 2 && parts[0] === tier.id;
          });

          if (
            keys.length > 0 ||
            on_sale_at ||
            off_sale_at ||
            start_at ||
            end_at ||
            code ||
            description
          ) {
            const ticket_type_update: any = { id: tier.id };
            console.log("Quantity: ", tier.quantity);
            keys.forEach((k) => {
              const key = k.split(".")[1];
              const value = dirtyText[k];

              if (
                key !== "quantity" ||
                (key === "quantity" && parseInt(value) > tier!.quantity)
              ) {
                ticket_type_update[key] = value;
              } else {
                console.log("Invalid quantity update: ", value);
              }
            });
            request.ticket_types.push(ticket_type_update);
          }
        }

        const result = await actions.ADMIN.updateTicketType(
          event_id!,
          ticket_type_id,
          request
        );
        if (result.update_id) {
          const params = new URLSearchParams(window.location.search);
          params.set("update_id", result.update_id);

          const newUrl =
            window.location.protocol +
            "//" +
            window.location.host +
            window.location.pathname +
            "?" +
            params.toString();
          window.history.pushState({ path: newUrl }, "", newUrl);

          const success = await pollForEventUpdateStatus();
          if (success) {
            toast({
              title: "Updated",
              description: "Ticket type has been updated",
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          }
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("tickettype", false);
      setEditingTicketType(null);
      setModalType(EditModalType.INACTIVE);
      updateDirtyText("all");
    },
    [event_id, dirtyText, fetchEvent, updateDirtyText, updateLoading, dialog]
  );

  const onLaunchEvent = useCallback(
    async (eventId: string) => {
      updateLoading("blocking", true);
      try {
        const result = await actions.ADMIN.launchEvent(eventId);
        if (result && result.update_id) {
          const params = new URLSearchParams(window.location.search);
          params.set("update_id", result.update_id);

          const newUrl =
            window.location.protocol +
            "//" +
            window.location.host +
            window.location.pathname +
            "?" +
            params.toString();
          window.history.pushState({ path: newUrl }, "", newUrl);

          const success = await pollForEventUpdateStatus();
          if (success) {
            toast({
              title:
                kydEvent?.status === "OPEN"
                  ? "✅ Event Updated"
                  : "✅ Event Launched",
              description:
                kydEvent?.status === "OPEN"
                  ? "Your event updates are complete"
                  : "Your event is now pending review",
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          }
        } else {
          await fetchEvent(eventId, { silent: true });
        }

        toast({
          title:
            kydEvent?.status === "OPEN"
              ? "✅ Event Updated"
              : "✅ Event Launched",
          description:
            kydEvent?.status === "OPEN"
              ? "Your event updates are complete"
              : "Your event is now pending review",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("blocking", false);
      updateDirtyText("all");
    },
    [event_id, pollForEventUpdateStatus, updateDirtyText, updateLoading, dialog]
  );

  const onCreateTicketType = useCallback(
    async (eventId: string) => {
      updateLoading("tickettype", true);
      try {
        const request: CreateTicketTypeRequest = {
          type: "GA",
          name: dirtyText["tickettype.name"],
          quantity: dirtyText["tickettype.quantity"],
          code: dirtyText["tickettype.code"] as string,
          price:
            dirtyText["tickettype.free"] === "true"
              ? "0"
              : dirtyText["tickettype.price"],
          limit: dirtyText["tickettype.limit"],
          description: dirtyText["tickettype.description"],
          on_sale_at: dirtyText["tickettype.on_sale_at"],
          off_sale_at: dirtyText["tickettype.off_sale_at"],
          free: dirtyText["tickettype.free"] === "true",
          mode: "website",
          start_at: dirtyText["tickettype.start_at"],
          end_at: dirtyText["tickettype.end_at"],
        };

        if (dirtyText["tickettype.price_mode"] === "boxoffice") {
          request.mode = "boxoffice";
          request.free = false;
          request.limit = "0";
          request.quantity = "0";

          delete request.on_sale_at;
          delete request.off_sale_at;
        } else if (dirtyText["tickettype.price_mode"] === "tiered_allocation") {
          request.tiers = dirtyText["tickettype.tiers"] || [];
        }

        await actions.ADMIN.createTicketType(event_id!, request);
        await fetchEvent(eventId, { silent: true });
        setEditingTicketType(null);
        setModalType(EditModalType.INACTIVE);
        updateDirtyText("all");

        toast({
          title: "✅ Ticket Type Created",
          description: "Ticket type has been created",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("tickettype", false);
    },
    [event_id, dirtyText, fetchEvent, updateLoading, dialog]
  );

  const onDeleteTicketType = useCallback(
    async (eventId: string, ticketTypeId: string) => {
      updateLoading("tickettype", true);
      try {
        await actions.ADMIN.deleteTicketType(event_id!, ticketTypeId);
        await fetchEvent(eventId, { silent: true });
        setEditingTicketType(null);
        setModalType(EditModalType.INACTIVE);
        updateDirtyText("all");

        toast({
          title: "✅ Ticket Type Draft Deleted",
          description: "Ticket type draft has been deleted",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("tickettype", false);
    },
    [event_id, dirtyText, fetchEvent, updateLoading]
  );

  const validateAndUpdateDirtyText = (dirtyText: object) => {
    const codeKey = Object.keys(dirtyText).find((val) => {
      const [_, afterSplit] = val.split(".");

      if (afterSplit === "code") {
        return val;
      }
    });
    const code = codeKey ? dirtyText[codeKey] : null;

    if (code?.length && code.length < 3) {
      setError({
        code: "Code needs to have at least 3 characters",
      });
    } else {
      setError({ code: "" });
    }

    updateDirtyText(dirtyText);
  };

  const renderDescription = (kyd: KYDAdminEvent) => {
    return (
      <Stack spacing={0}>
        <HStack>
          <Text fontWeight={"bold"}>Description</Text>
          <IconButton
            isDisabled={loading["blocking"]}
            onClick={() => setModalType(EditModalType.DESCRIPTION)}
            icon={<FiEdit2 fontSize="1.25rem" />}
            variant="ghost"
            aria-label="Edit member"
          />
        </HStack>
        {kyd.description ? (
          <Box
            borderWidth={"1px"}
            borderColor="gray.200"
            borderRadius={"lg"}
            p={4}
          >
            <Text
              w="100%"
              whiteSpace="pre-wrap"
              fontFamily="Work Sans"
              as="pre"
            >
              {kyd.description}
            </Text>
          </Box>
        ) : (
          <Text color="gray.500">No description added yet</Text>
        )}
      </Stack>
    );
  };

  const renderTicketTypes = (kyd: KYDAdminEvent) => {
    return (
      <Stack spacing={0}>
        <Stack
          direction={{ base: "column", md: "row" }}
          justify={"space-between"}
          w="100%"
        >
          <Text fontWeight={"bold"}>Ticket Types</Text>
          <HStack>
            <Button
              isDisabled={!kydEvent?.report_link}
              onClick={() => window.open(kydEvent?.report_link, "_blank")}
              w={["100%", "230px"]}
              aria-label="Download event report"
              variant={"outline"}
              leftIcon={<FaCloudDownloadAlt />}
            >
              Download Sales Report
            </Button>
            <IconButton
              isDisabled={loading["blocking"]}
              variant={"outline"}
              aria-label="Add ticket type"
              icon={<FaPlus />}
              onClick={() => {
                setEditingTicketType(null);
                setModalType(EditModalType.TICKET_TYPE);
              }}
            />
          </HStack>
        </Stack>
        <Box
          py={4}
          overflowX={
            (kydEvent?.ticket_types || []).length === 0 ? "inherit" : "scroll"
          }
        >
          {(kydEvent?.ticket_types || []).length === 0 ? (
            <Text color="gray.500">No ticket types created yet</Text>
          ) : null}
          {(kydEvent?.ticket_types || []).length > 0 ? (
            <TicketTypesTable
              kydEvent={kydEvent!}
              onEdit={renderEditModal}
              ticketTypes={kyd.ticket_types}
              tiers={tiers}
              loading={loading}
            />
          ) : null}
        </Box>
      </Stack>
    );
  };

  const renderCampaignsTable = (kyd: KYDAdminEvent) => {
    return (
      <Stack>
        <Stack
          direction={{ base: "column", md: "row" }}
          justify={"space-between"}
          w="100%"
        >
          <Text fontWeight={"bold"}>Campaigns</Text>
        </Stack>
        <Box
          py={4}
          overflowX={(kyd?.campaigns || []).length === 0 ? "inherit" : "scroll"}
        >
          {(kyd?.campaigns || []).length === 0 ? (
            <Text color="gray.500">
              No campaigns created. Campaigns will populate here when the first
              click from the campaign is recognized.
            </Text>
          ) : null}
          {(kyd?.campaigns || []).length > 0 ? (
            <CampaignsTable kydEvent={kyd} />
          ) : null}
        </Box>
      </Stack>
    );
  };

  const renderPromoCodes = (kyd: KYDAdminEvent) => {
    return (
      <Stack>
        <HStack justify={"space-between"} w="100%">
          <Text fontWeight={"bold"}>Promo Codes</Text>
          <IconButton
            isDisabled={loading["blocking"]}
            variant={"outline"}
            aria-label="Add promo code"
            icon={<FaPlus />}
            onClick={() => setModalType(EditModalType.PROMO_CODE)}
          />
        </HStack>
        <Box
          py={4}
          overflowX={kyd.promo_codes.length === 0 ? "inherit" : "scroll"}
        >
          {kyd.promo_codes.length === 0 ? (
            <Text color="gray.500">No promo codes created</Text>
          ) : null}
          {kyd.promo_codes.length > 0 ? (
            <PromoCodesTable
              onEdit={(promoCode) => {
                setEditingPromoCode(promoCode);
                setModalType(EditModalType.PROMO_CODE);
              }}
              promoCodes={kyd.promo_codes}
              loading={loading}
            />
          ) : null}
        </Box>
      </Stack>
    );
  };

  const renderAffiliates = (kyd: KYDAdminEvent) => {
    return (
      <Stack>
        <Stack
          direction={{ base: "column", md: "row" }}
          justify={"space-between"}
          w="100%"
        >
          <Text fontWeight={"bold"}>Affiliates</Text>
          <HStack>
            {kydEvent ? (
              <Button
                isDisabled={!kydEvent.affiliates_report_link}
                onClick={() =>
                  window.open(kydEvent.affiliates_report_link, "_blank")
                }
                w={["100%", "250px"]}
                aria-label="Download affiliate report"
                variant={"outline"}
                leftIcon={<FaCloudDownloadAlt />}
              >
                Download Affiliate Report
              </Button>
            ) : null}
            <IconButton
              isDisabled={loading["blocking"]}
              variant={"outline"}
              aria-label="Add affiliates"
              icon={<FaPlus />}
              onClick={() => setModalType(EditModalType.CREATE_AFFILIATES)}
            />
          </HStack>
        </Stack>
        <Text color="gray.500" fontSize={"sm"}>
          Affiliate data is updated once per day automatically
        </Text>
        <Box
          py={4}
          overflowX={kyd.affiliates.length === 0 ? "inherit" : "scroll"}
        >
          {kyd.affiliates.length === 0 ? (
            <Text color="gray.500">No affiliates created</Text>
          ) : null}
          {kyd.affiliates.length > 0 ? (
            <AffiliatesTable
              onEdit={(affiliate) => {
                setEditingAffiliate(affiliate);
              }}
              onDelete={(affiliate) => {
                setDeleteAffiliate(affiliate);
              }}
              affiliates={kyd.affiliates}
              loading={loading}
            />
          ) : null}
        </Box>
      </Stack>
    );
  };

  const renderMarketing = (kyd: KYDAdminEvent) => {
    return (
      <Stack>
        <Stack
          direction={{ base: "column", md: "row" }}
          justify={"space-between"}
          w="100%"
        >
          <Text fontWeight={"bold"}>Marketing Links</Text>
        </Stack>
        <Box py={4} overflowX={"scroll"}>
          {kyd ? <MarketingTable kydEvent={kyd} /> : null}
        </Box>
      </Stack>
    );
  };

  const renderStat = (props: Props) => {
    const { label, value, ...boxProps } = props;
    return (
      <Box
        px={{ base: "4", md: "4" }}
        py={{ base: "5", md: "4" }}
        bg="bg-surface"
        borderRadius="lg"
        borderWidth={"1px"}
        borderColor="gray.200"
        {...boxProps}
      >
        <Stack>
          <Text fontSize="sm" color="muted">
            {label}
          </Text>
          <Heading size={headerSize}>{value}</Heading>
        </Stack>
      </Box>
    );
  };

  const renderEditDescriptionModal = () => (
    <Modal
      size={"3xl"}
      isOpen={true}
      onClose={() => setModalType(EditModalType.INACTIVE)}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Description</ModalHeader>
        <ModalBody>
          <Textarea
            isDisabled={loading["event"]}
            onChange={(e) => {
              updateDirtyText({ description: e.target.value });
            }}
            value={
              `description` in dirtyText
                ? dirtyText[`description`]
                : kydEvent?.description
            }
            minH={"400px"}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={loading["event"]}
            onClick={() => {
              updateDirtyText("all");
              setModalType(EditModalType.INACTIVE);
            }}
            mr="4"
          >
            Cancel
          </Button>
          <Button
            color="white"
            bg="green.500"
            isLoading={loading["event"]}
            onClick={() => onUpdateEvent("description")}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const renderEditNameModal = () => (
    <Modal
      size={"3xl"}
      isOpen={true}
      onClose={() => setModalType(EditModalType.INACTIVE)}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Event Name</ModalHeader>
        <ModalBody>
          <Input
            isDisabled={loading["event"]}
            onChange={(e) => {
              updateDirtyText({ name: e.target.value });
            }}
            value={`name` in dirtyText ? dirtyText[`name`] : kydEvent?.name}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={loading["event"]}
            onClick={() => {
              updateDirtyText("all");
              setModalType(EditModalType.INACTIVE);
            }}
            mr="4"
          >
            Cancel
          </Button>
          <Button
            color="white"
            bg="green.500"
            isLoading={loading["event"]}
            onClick={() => onUpdateEvent("name")}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const renderEditVenueModal = () => (
    <Modal
      size={"3xl"}
      isOpen={true}
      onClose={() => setModalType(EditModalType.INACTIVE)}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Venue</ModalHeader>
        <ModalBody>
          <Stack>
            <Stack>
              <Text fontWeight={"medium"}>Venue Name</Text>
              <Input
                placeholder="Venue name"
                isDisabled={loading["event"]}
                onChange={(e) => {
                  updateDirtyText({ "venue.name": e.target.value });
                }}
                value={
                  `venue.name` in dirtyText
                    ? dirtyText[`venue.name`]
                    : kydEvent?.venues[0].name
                }
              />
            </Stack>
            <Stack>
              <Text fontWeight={"medium"}>Venue Address</Text>
              <Input
                placeholder="Venue address"
                isDisabled={loading["event"]}
                onChange={(e) => {
                  updateDirtyText({ "venue.address": e.target.value });
                }}
                value={
                  `venue.address` in dirtyText
                    ? dirtyText[`venue.address`]
                    : kydEvent?.venues[0].address
                }
              />
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={loading["event"]}
            onClick={() => {
              updateDirtyText("all");
              setModalType(EditModalType.INACTIVE);
            }}
            mr="4"
          >
            Cancel
          </Button>
          <Button
            color="white"
            bg="green.500"
            isLoading={loading["event"]}
            isDisabled={
              (dirtyText["venue.name"] ? dirtyText["venue.name"].length : "")
                .length === 0
            }
            onClick={() => {
              const params = {
                venue_name: dirtyText["venue.name"] || kydEvent?.venues[0].name,
                venue_address:
                  dirtyText["venue.address"] || kydEvent?.venues[0].address,
              };
              onUpdateEvent("venue", JSON.stringify(params));
            }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const onCloseEditTieredTicketTypeModal = () => {
    console.log("ran");
    setEditingTicketType(null);
    updateDirtyText("all");
    setModalType(EditModalType.INACTIVE);
  };

  const renderEditTicketTypeModal = (ticketType: AdminTicketType | null) => {
    return (
      <Modal
        size={"xl"}
        isOpen={true}
        onClose={() => {
          onCloseEditTieredTicketTypeModal();
        }}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {ticketType ? "Edit Ticket Type" : "Create Ticket Type"}
          </ModalHeader>
          <ModalBody>
            <Stack spacing={6}>
              <Stack>
                <Text fontWeight={"medium"}>Name</Text>
                <Input
                  placeholder="Ticket type name"
                  isDisabled={loading["tickettype"]}
                  onChange={(e) => {
                    updateDirtyText({
                      [`${ticketType ? ticketType.id : "tickettype"}.name`]:
                        e.target.value,
                    });
                  }}
                  value={
                    `${ticketType ? ticketType.id : "tickettype"}.name` in
                    dirtyText
                      ? dirtyText[
                          `${ticketType ? ticketType.id : "tickettype"}.name`
                        ]
                      : ticketType
                      ? ticketType.name
                      : null
                  }
                />
              </Stack>
              <Stack>
                <Text fontWeight={"medium"}>Description</Text>
                <Textarea
                  placeholder="Ticket type description"
                  isDisabled={loading["tickettype"]}
                  onChange={(e) => {
                    updateDirtyText({
                      [`${
                        ticketType ? ticketType.id : "tickettype"
                      }.description`]: e.target.value,
                    });
                  }}
                  value={
                    `${
                      ticketType ? ticketType.id : "tickettype"
                    }.description` in dirtyText
                      ? dirtyText[
                          `${
                            ticketType ? ticketType.id : "tickettype"
                          }.description`
                        ]
                      : ticketType
                      ? ticketType.description
                      : null
                  }
                />
              </Stack>

              <Stack
                display={
                  (ticketType && ticketType.code === "boxoffice") ||
                  dirtyText["tickettype.price_mode"] === "boxoffice"
                    ? "none"
                    : "inherit"
                }
                spacing={2}
              >
                <Text fontSize={"xl"} fontWeight={"bold"}>
                  Timeline
                </Text>
                <Divider />

                <Stack
                  direction={{ base: "column", md: "row" }}
                  align={"center"}
                  spacing={2}
                >
                  <Stack maxW={"250px"} w="100%" spacing={0}>
                    <HStack>
                      <Text fontWeight={"medium"}>On Sale Date</Text>
                      <Text fontSize={"sm"} color={"gray.500"}>
                        {"(Optional)"}
                      </Text>
                    </HStack>
                    <DateTimePicker
                      disablePast
                      maxDate={dayjs(kydEvent?.end_at).tz(kydEvent?.timezone)}
                      defaultValue={
                        `${
                          ticketType ? ticketType.id : "tickettype"
                        }.on_sale_at` in dirtyText &&
                        dirtyText[
                          `${
                            ticketType ? ticketType.id : "tickettype"
                          }.on_sale_at`
                        ] !== "_DELETE_"
                          ? dayjs(
                              dirtyText[
                                `${
                                  ticketType ? ticketType.id : "tickettype"
                                }.on_sale_at`
                              ]
                            )
                          : ticketType && ticketType.on_sale_at
                          ? dayjs(ticketType.on_sale_at).tz(kydEvent?.timezone)
                          : null
                      }
                      timezone={kydEvent?.timezone}
                      onChange={(date) => {
                        if (date === null) {
                          updateDirtyText({
                            [`${
                              ticketType ? ticketType.id : "tickettype"
                            }.on_sale_at`]: "_DELETE_",
                          });
                        } else {
                          updateDirtyText({
                            [`${
                              ticketType ? ticketType.id : "tickettype"
                            }.on_sale_at`]: date?.toISOString(),
                          });
                        }
                      }}
                      slotProps={{
                        actionBar: {
                          actions: ["clear", "accept"],
                        },
                      }}
                    />
                    <Text fontSize="sm" color="gray.500">
                      {kydEvent?.timezone}
                    </Text>
                  </Stack>
                  <Stack w="100%" maxW={"250px"} spacing={0}>
                    <HStack>
                      <Text fontWeight={"medium"}>Off Sale Date</Text>
                      <Text fontSize={"sm"} color={"gray.500"}>
                        {"(Optional)"}
                      </Text>
                    </HStack>
                    <DateTimePicker
                      disablePast
                      maxDate={dayjs(kydEvent?.end_at).tz(kydEvent?.timezone)}
                      defaultValue={
                        `${
                          ticketType ? ticketType.id : "tickettype"
                        }.off_sale_at` in dirtyText &&
                        dirtyText[
                          `${
                            ticketType ? ticketType.id : "tickettype"
                          }.off_sale_at`
                        ] === "_DELETE_"
                          ? dayjs(
                              dirtyText[
                                `${
                                  ticketType ? ticketType.id : "tickettype"
                                }.off_sale_at`
                              ]
                            )
                          : ticketType && ticketType.off_sale_at
                          ? dayjs(ticketType.off_sale_at).tz(kydEvent?.timezone)
                          : null
                      }
                      timezone={kydEvent?.timezone}
                      onChange={(date) => {
                        if (date === null) {
                          updateDirtyText({
                            [`${
                              ticketType ? ticketType.id : "tickettype"
                            }.off_sale_at`]: "_DELETE_",
                          });
                        } else {
                          updateDirtyText({
                            [`${
                              ticketType ? ticketType.id : "tickettype"
                            }.off_sale_at`]: date?.toISOString(),
                          });
                        }
                      }}
                      slotProps={{
                        actionBar: {
                          actions: ["clear", "accept"],
                        },
                      }}
                    />
                    <Text fontSize="sm" color="gray.500">
                      {kydEvent?.timezone}
                    </Text>
                  </Stack>
                </Stack>

                <Stack
                  direction={{ base: "column", md: "row" }}
                  align={"center"}
                  spacing={2}
                >
                  <Stack maxW={"250px"} w="100%" spacing={0}>
                    <HStack>
                      <Text fontWeight={"medium"}>Valid From Date</Text>
                      <Text fontSize={"sm"} color={"gray.500"}>
                        {"(Optional)"}
                      </Text>
                    </HStack>
                    <DateTimePicker
                      minDate={dayjs(kydEvent?.start_at).tz(kydEvent?.timezone)}
                      maxDate={dayjs(kydEvent?.end_at).tz(kydEvent?.timezone)}
                      defaultValue={
                        `${
                          ticketType ? ticketType.id : "tickettype"
                        }.start_at` in dirtyText &&
                        dirtyText[
                          `${
                            ticketType ? ticketType.id : "tickettype"
                          }.start_at`
                        ] !== "_DELETE_"
                          ? dayjs(
                              dirtyText[
                                `${
                                  ticketType ? ticketType.id : "tickettype"
                                }.start_at`
                              ]
                            )
                          : ticketType && ticketType.start_at
                          ? dayjs(ticketType.start_at)
                          : null
                      }
                      timezone={kydEvent?.timezone}
                      onChange={(date) => {
                        if (date === null) {
                          updateDirtyText({
                            [`${
                              ticketType ? ticketType.id : "tickettype"
                            }.start_at`]: "_DELETE_",
                          });
                        } else {
                          updateDirtyText({
                            [`${
                              ticketType ? ticketType.id : "tickettype"
                            }.start_at`]: date?.toISOString(),
                          });
                        }
                      }}
                      slotProps={{
                        actionBar: {
                          actions: ["clear", "accept"],
                        },
                      }}
                    />

                    <Text fontSize="sm" color="gray.500">
                      {kydEvent?.timezone}
                    </Text>
                  </Stack>
                  <Stack w="100%" maxW={"250px"} spacing={0}>
                    <HStack>
                      <Text fontWeight={"medium"}>Valid To Date</Text>
                      <Text fontSize={"sm"} color={"gray.500"}>
                        {"(Optional)"}
                      </Text>
                    </HStack>{" "}
                    <DateTimePicker
                      minDate={dayjs(kydEvent?.start_at).tz(kydEvent?.timezone)}
                      maxDate={dayjs(kydEvent?.end_at).tz(kydEvent?.timezone)}
                      defaultValue={
                        `${ticketType ? ticketType.id : "tickettype"}.end_at` in
                          dirtyText &&
                        dirtyText[
                          `${ticketType ? ticketType.id : "tickettype"}.end_at`
                        ] !== "_DELETE_"
                          ? dayjs(
                              dirtyText[
                                `${
                                  ticketType ? ticketType.id : "tickettype"
                                }.end_at`
                              ]
                            )
                          : ticketType && ticketType.end_at
                          ? dayjs(ticketType.end_at)
                          : null
                      }
                      timezone={kydEvent?.timezone}
                      onChange={(date) => {
                        if (date === null) {
                          updateDirtyText({
                            [`${
                              ticketType ? ticketType.id : "tickettype"
                            }.end_at`]: "_DELETE_",
                          });
                        } else {
                          updateDirtyText({
                            [`${
                              ticketType ? ticketType.id : "tickettype"
                            }.end_at`]: date?.toISOString(),
                          });
                        }
                      }}
                      slotProps={{
                        actionBar: {
                          actions: ["clear", "accept"],
                        },
                      }}
                    />
                    <Text fontSize="sm" color="gray.500">
                      {kydEvent?.timezone}
                    </Text>
                  </Stack>
                </Stack>
              </Stack>
              {/*// PRICING //*/}
              <Stack>
                <Stack>
                  <Text fontSize={"xl"} fontWeight={"bold"}>
                    Pricing
                  </Text>
                  <Divider />
                </Stack>
                <Stack spacing={0}>
                  {!ticketType ? (
                    <ButtonGroup size="sm" isAttached variant="outline">
                      <Button
                        h="40px"
                        rounded={"none"}
                        _hover={{
                          backgroundColor:
                            !dirtyText["tickettype.price_mode"] ||
                            dirtyText["tickettype.price_mode"] === "single"
                              ? "gray.800"
                              : "gray.200",
                        }}
                        bg={
                          !dirtyText["tickettype.price_mode"] ||
                          dirtyText["tickettype.price_mode"] === "single"
                            ? "black"
                            : "white"
                        }
                        color={
                          !dirtyText["tickettype.price_mode"] ||
                          dirtyText["tickettype.price_mode"] === "single"
                            ? "white"
                            : "black"
                        }
                        onClick={() =>
                          updateDirtyText({
                            [`tickettype.price_mode`]: "single",
                          })
                        }
                      >
                        Single price
                      </Button>
                      <Button
                        rounded={"none"}
                        h="40px"
                        _hover={{
                          backgroundColor:
                            dirtyText["tickettype.price_mode"] ===
                            "tiered_allocation"
                              ? "gray.800"
                              : "gray.200",
                        }}
                        isDisabled={
                          dirtyText["tickettype.mode"] === "boxoffice"
                        }
                        bg={
                          dirtyText["tickettype.price_mode"] ===
                          "tiered_allocation"
                            ? "black"
                            : "white"
                        }
                        color={
                          dirtyText["tickettype.price_mode"] ===
                          "tiered_allocation"
                            ? "white"
                            : "black"
                        }
                        onClick={() =>
                          updateDirtyText({
                            [`tickettype.price_mode`]: "tiered_allocation",
                          })
                        }
                      >
                        Price tiers by allocation
                      </Button>
                      <Button
                        display={
                          kydEvent?.currency === "USD" ? "inherit" : "none"
                        }
                        rounded={"none"}
                        h="40px"
                        _hover={{
                          backgroundColor:
                            dirtyText["tickettype.price_mode"] === "boxoffice"
                              ? "gray.800"
                              : "gray.200",
                        }}
                        isDisabled={
                          dirtyText["tickettype.mode"] === "boxoffice"
                        }
                        bg={
                          dirtyText["tickettype.price_mode"] === "boxoffice"
                            ? "black"
                            : "white"
                        }
                        color={
                          dirtyText["tickettype.price_mode"] === "boxoffice"
                            ? "white"
                            : "black"
                        }
                        onClick={() =>
                          updateDirtyText({
                            [`tickettype.price_mode`]: "boxoffice",
                          })
                        }
                      >
                        {`Box Office (POS)`}
                      </Button>
                    </ButtonGroup>
                  ) : null}
                </Stack>
                <HStack w="100%" align={"top"} justify={"space-between"}>
                  <Stack
                    display={
                      dirtyText["tickettype.price_mode"] !== "tiered_allocation"
                        ? "inherit"
                        : "none"
                    }
                  >
                    <Text fontWeight={"medium"}>Price</Text>
                    <HStack>
                      <HStack>
                        <CurrencyInput
                          //@ts-ignore
                          maxW="150px"
                          value={
                            `${
                              ticketType ? ticketType.id : "tickettype"
                            }.price` in dirtyText
                              ? dirtyText[
                                  `${
                                    ticketType ? ticketType.id : "tickettype"
                                  }.price`
                                ]
                              : ticketType
                              ? ticketType.price.units
                              : null
                          }
                          onValueChange={(value) => {
                            updateDirtyText({
                              [`${
                                ticketType ? ticketType.id : "tickettype"
                              }.price`]: value,
                            });
                          }}
                          placeholder="Price"
                          intlConfig={{
                            locale: "en-US",
                            currency: kydEvent?.currency,
                          }}
                          customInput={Input}
                          step={1}
                        />
                        <Text>{kydEvent?.currency}</Text>
                      </HStack>
                      {!ticketType &&
                      dirtyText["tickettype.price_mode"] !== "boxoffice" ? (
                        <HStack>
                          <Divider
                            borderWidth={"1px"}
                            h="30px"
                            orientation="vertical"
                          />
                          <Checkbox
                            onChange={(e) => {
                              updateDirtyText({
                                "tickettype.free": e.target.checked
                                  ? "true"
                                  : "false",
                                "tickettype.price": "",
                              });
                            }}
                          >
                            <Text fontWeight={"medium"}>Free</Text>
                          </Checkbox>
                        </HStack>
                      ) : null}
                    </HStack>
                  </Stack>
                  <Stack
                    display={
                      dirtyText["tickettype.price_mode"] === "boxoffice"
                        ? "none"
                        : "inherit"
                    }
                  >
                    <Text fontWeight={"medium"}>Limit Per Customer</Text>
                    <Input
                      placeholder="Limit"
                      isDisabled={loading["tickettype"]}
                      maxW={"100px"}
                      inputMode="tel"
                      value={
                        `${ticketType ? ticketType.id : "tickettype"}.limit` in
                        dirtyText
                          ? dirtyText[
                              `${
                                ticketType ? ticketType.id : "tickettype"
                              }.limit`
                            ]
                          : ticketType
                          ? ticketType.limit
                          : null
                      }
                      onChange={(e) =>
                        updateDirtyText({
                          [`${
                            ticketType ? ticketType.id : "tickettype"
                          }.limit`]: e.target.value,
                        })
                      }
                    />
                    <Text fontSize="sm" color="gray.500">
                      (Max 10)
                    </Text>
                  </Stack>
                </HStack>
                <Stack
                  display={
                    (ticketType && ticketType.code !== "boxoffice") ||
                    (!ticketType &&
                      (!dirtyText["tickettype.price_mode"] ||
                        dirtyText["tickettype.price_mode"] === "single"))
                      ? "inherit"
                      : "none"
                  }
                >
                  <Text fontWeight={"medium"}>Quantity</Text>
                  <HStack>
                    <Input
                      placeholder="Quantity"
                      borderColor={
                        ticketType
                          ? `${ticketType.id}.quantity` in dirtyText &&
                            parseInt(dirtyText[`${ticketType.id}.quantity`]) <
                              ticketType.quantity - ticketType.remaining
                            ? "red.500"
                            : "gray.200"
                          : "gray.200"
                      }
                      borderWidth={
                        ticketType
                          ? `${ticketType.id}.quantity` in dirtyText &&
                            parseInt(dirtyText[`${ticketType.id}.quantity`]) <
                              ticketType.quantity - ticketType.remaining
                            ? "2px"
                            : "1px"
                          : "1px"
                      }
                      color={
                        ticketType
                          ? `${ticketType.id}.quantity` in dirtyText &&
                            parseInt(dirtyText[`${ticketType.id}.quantity`]) <
                              ticketType.quantity - ticketType.remaining
                            ? "red.500"
                            : "inherit"
                          : "inherit"
                      }
                      isDisabled={loading["tickettype"]}
                      maxW={"100px"}
                      inputMode="tel"
                      onChange={(e) => {
                        updateDirtyText({
                          [`${
                            ticketType ? ticketType.id : "tickettype"
                          }.quantity`]: e.target.value,
                        });
                      }}
                      value={
                        `${
                          ticketType ? ticketType.id : "tickettype"
                        }.quantity` in dirtyText
                          ? dirtyText[
                              `${
                                ticketType ? ticketType.id : "tickettype"
                              }.quantity`
                            ]
                          : ticketType
                          ? Math.floor(ticketType.quantity)
                          : null
                      }
                    />
                    <Text
                      color={
                        ticketType
                          ? `${ticketType.id}.quantity` in dirtyText &&
                            parseInt(dirtyText[`${ticketType.id}.quantity`]) <
                              ticketType.quantity - ticketType.remaining
                            ? "red.500"
                            : "inherit"
                          : "inherit"
                      }
                    >
                      tickets
                    </Text>
                  </HStack>
                  {ticketType
                    ? `${ticketType.id}.quantity` in dirtyText &&
                      parseInt(dirtyText[`${ticketType.id}.quantity`]) <
                        ticketType.quantity - ticketType.remaining && (
                        <Text color="red.500">
                          Ticket quantity must be greater than or equal to{" "}
                          {ticketType.quantity - ticketType.remaining} tickets
                        </Text>
                      )
                    : null}
                </Stack>
              </Stack>

              <Stack
                display={
                  dirtyText["tickettype.price_mode"] === "tiered_allocation"
                    ? "inherit"
                    : "none"
                }
              >
                {dirtyText["tickettype.tiers"] ||
                (ticketType && tiers[ticketType.id]) ? (
                  <Stack w="100%">
                    <HStack w="100%">
                      <Text fontWeight={"bold"} w="31%">
                        Name
                      </Text>
                      <Text fontWeight={"bold"} w="31%">
                        Quantity
                      </Text>
                      <Text fontWeight={"bold"} w="38%">
                        Price
                      </Text>
                    </HStack>
                    {dirtyText["tickettype.tiers"]?.map(
                      (tier: KYDTicketTypeTierRequest, index: number) => (
                        <HStack w="100%" key={index}>
                          <Input
                            w="31%"
                            placeholder="Name"
                            value={tier.name}
                            onChange={(e) => {
                              let tiers = dirtyText["tickettype.tiers"];
                              let this_tier = tiers[index];
                              this_tier.name = e.target.value;
                              tiers[index] = this_tier;
                              updateDirtyText({ "tickettype.tiers": tiers });
                            }}
                          />
                          <Input
                            w="31%"
                            placeholder="Quantity"
                            value={tier.quantity}
                            inputMode="tel"
                            onChange={(e) => {
                              let tiers = dirtyText["tickettype.tiers"];
                              let this_tier = tiers[index];
                              this_tier.quantity = e.target.value;
                              tiers[index] = this_tier;
                              updateDirtyText({ "tickettype.tiers": tiers });
                            }}
                          />
                          <CurrencyInput
                            //@ts-ignore
                            w="31%"
                            value={tier.price}
                            onValueChange={(value) => {
                              let tiers = dirtyText["tickettype.tiers"];
                              let this_tier = tiers[index];
                              this_tier.price = value;
                              tiers[index] = this_tier;
                              updateDirtyText({ "tickettype.tiers": tiers });
                            }}
                            placeholder="Price"
                            intlConfig={{
                              locale: "en-US",
                              currency: kydEvent?.currency,
                            }}
                            customInput={Input}
                            step={1}
                          />
                          <IconButton
                            w="9%"
                            icon={<FaTrashAlt fontSize={"1.0rem"} />}
                            aria-label="Delete tier"
                            variant={"ghost"}
                            onClick={() => {
                              let tiers = (
                                dirtyText[
                                  "tickettype.tiers"
                                ] as Array<KYDTicketTypeTierRequest>
                              ).filter((_, i) => i !== index);
                              updateDirtyText({ "tickettype.tiers": tiers });
                            }}
                          />
                        </HStack>
                      )
                    )}
                  </Stack>
                ) : null}
                {!dirtyText["tickettype.tiers"] ||
                dirtyText["tickettype.tiers"].length <= 5 ? (
                  <Button
                    leftIcon={<FiPlus />}
                    onClick={() => {
                      const tiers = dirtyText["tickettype.tiers"] || [];
                      const tier: KYDTicketTypeTierRequest = {
                        name: "",
                        quantity: "",
                        price: "",
                      };
                      tiers.push(tier);
                      updateDirtyText({ "tickettype.tiers": tiers });
                    }}
                  >
                    Add a price tier
                  </Button>
                ) : null}
              </Stack>
              <CodeSection
                error={error.code}
                dirtyText={dirtyText}
                isLoading={loading.tickettype}
                ticketType={ticketType}
                updateDirtyText={validateAndUpdateDirtyText}
              />
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button
              isDisabled={loading["tickettype"]}
              onClick={() => {
                onCloseEditTieredTicketTypeModal();
              }}
              mr="4"
            >
              Cancel
            </Button>
            {ticketType && ticketType.status === "DRAFT" ? (
              <Button
                isLoading={loading["tickettype"]}
                bg="red.500"
                color="white"
                mr="4"
                onClick={() => onDeleteTicketType(event_id!, ticketType.id)}
              >
                Delete
              </Button>
            ) : null}
            {ticketType ? (
              <Button
                isDisabled={
                  (`${ticketType.id}.quantity` in dirtyText &&
                    parseInt(dirtyText[`${ticketType.id}.quantity`]) <
                      ticketType.quantity - ticketType.remaining) ||
                  (`${ticketType.id}.limit` in dirtyText &&
                    parseInt(dirtyText[`${ticketType.id}.limit`]) > 10) ||
                  !!error.code
                }
                color="white"
                bg="green.500"
                isLoading={loading["tickettype"]}
                onClick={() => onUpdateTicketType(editingTicketType!.id)}
              >
                Save
              </Button>
            ) : (
              <Button
                bg="blue.500"
                color="white"
                isLoading={loading["tickettype"]}
                onClick={() => onCreateTicketType(event_id!)}
              >
                Create
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

  const renderEditPromoCode = (
    kydEvent: KYDAdminEvent,
    promoCode: KYDPromoCode | null
  ) => (
    <Modal
      size={"xl"}
      isOpen={true}
      onClose={() => setModalType(EditModalType.INACTIVE)}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {promoCode ? "Edit Promo Code" : "Create Promo Code"}
        </ModalHeader>
        <ModalBody>
          <Stack spacing={4}>
            <HStack>
              <Stack spacing={0}>
                <Text fontWeight={"medium"}>Code</Text>
                <InputGroup size={"md"}>
                  <Input
                    pr="6rem"
                    isDisabled={loading["promocode"]}
                    onChange={(e) => {
                      updateDirtyText({ "promocode.code": e.target.value });
                    }}
                    value={
                      `promocode.code` in dirtyText
                        ? dirtyText[`promocode.code`]
                        : promoCode
                        ? promoCode.code
                        : null
                    }
                  />
                  {!promoCode ? (
                    <InputRightElement width="6rem">
                      <Button
                        onClick={() => {
                          const s = getRandomString(10).toUpperCase();
                          updateDirtyText({ "promocode.code": s });
                        }}
                        h="1.75rem"
                        size="sm"
                      >
                        Generate
                      </Button>
                    </InputRightElement>
                  ) : null}
                </InputGroup>
              </Stack>
              <Stack spacing={0}>
                <Text fontWeight={"medium"}>Enabled</Text>
                <HStack>
                  <Switch
                    isDisabled={
                      promoCode && promoCode.status === "expired" ? true : false
                    }
                    defaultChecked={
                      (promoCode && promoCode.status === "active") ||
                      dirtyText[`promocode.enabled`] === "true"
                    }
                    onChange={(e) =>
                      updateDirtyText({
                        "promocode.enabled":
                          (promoCode && promoCode.status === "active") ||
                          dirtyText[`promocode.enabled`] === "true"
                            ? "false"
                            : "true",
                      })
                    }
                    size={"lg"}
                    colorScheme={"green"}
                  />
                  <Text>
                    {dirtyText[`promocode.enabled`] === "true" ||
                    (!(`promocode.enabled` in dirtyText) &&
                      promoCode?.status === "active")
                      ? "Yes"
                      : "No"}
                  </Text>
                </HStack>
              </Stack>
            </HStack>

            <Stack spacing={0}>
              <Text fontWeight={"medium"}>Discount</Text>
              <HStack>
                <Select
                  onChange={(e) => {
                    updateDirtyText({ "promocode.mode": e.target.value });
                  }}
                  defaultValue={
                    promoCode ? promoCode.mode : dirtyText[`promocode.mode`]
                  }
                  maxW="200px"
                >
                  <option value={"percent"}>Percent Off</option>
                  <option value={"fixed"}>Fixed Amount</option>
                </Select>
                <InputGroup>
                  <Input
                    type={"number"}
                    isDisabled={loading["promocode"]}
                    onChange={(e) => {
                      updateDirtyText({ "promocode.amount": e.target.value });
                    }}
                    value={
                      `promocode.amount` in dirtyText
                        ? dirtyText[`promocode.amount`]
                        : promoCode
                        ? Math.floor(promoCode.amount / 100)
                        : null
                    }
                  />
                  <InputRightAddon
                    children={
                      "promocode.mode" in dirtyText
                        ? dirtyText[`promocode.mode`] === "percent"
                          ? "%"
                          : kydEvent.currency
                        : promoCode && promoCode.mode === "fixed"
                        ? kydEvent.currency
                        : "%"
                    }
                  />
                </InputGroup>
              </HStack>
            </Stack>

            <Stack spacing={0}>
              <Text fontWeight={"medium"}>Ticket Types</Text>
              <Stack
                rounded={"lg"}
                borderWidth={"1px"}
                borderColor={"gray.300"}
                p={2}
                maxH={"300px"}
                overflowY={"scroll"}
              >
                {kydEvent.ticket_types.map((tt) => (
                  <HStack
                    justify={"space-between"}
                    h="50px"
                    p={2}
                    rounded={"lg"}
                    borderWidth={"1px"}
                    key={tt.id}
                    cursor={"pointer"}
                    onClick={() => {
                      updateDirtyText({
                        [`promocode.${tt.id}`]:
                          `promocode.${tt.id}` in dirtyText
                            ? dirtyText[`promocode.${tt.id}`] === "true"
                              ? "false"
                              : "true"
                            : promoCode &&
                              promoCode.ticket_type_ids.includes(tt.id)
                            ? "false"
                            : "true",
                      });
                    }}
                  >
                    <Text
                      fontWeight={
                        `promocode.${tt.id}` in dirtyText
                          ? dirtyText[`promocode.${tt.id}`] === "true"
                            ? "bold"
                            : "normal"
                          : promoCode &&
                            promoCode.ticket_type_ids.includes(tt.id)
                          ? "bold"
                          : "normal"
                      }
                    >
                      {tt.name}
                    </Text>
                    <Icon
                      display={
                        `promocode.${tt.id}` in dirtyText
                          ? dirtyText[`promocode.${tt.id}`] === "true"
                            ? "inherit"
                            : "none"
                          : promoCode &&
                            promoCode.ticket_type_ids.includes(tt.id)
                          ? "inherit"
                          : "none"
                      }
                      boxSize={8}
                      color={"green.500"}
                      as={FaCheckCircle}
                    />
                  </HStack>
                ))}
              </Stack>
            </Stack>
            <Stack spacing={0}>
              <Text fontWeight={"medium"}>Max Number Of Uses</Text>
              <Input
                placeholder="Optional"
                type={"number"}
                isDisabled={loading["promocode"]}
                onChange={(e) => {
                  updateDirtyText({
                    "promocode.max_total_uses": e.target.value,
                  });
                }}
                value={
                  `promocode.max_total_uses` in dirtyText
                    ? dirtyText[`promocode.max_total_uses`]
                    : promoCode
                    ? promoCode.max_total_uses
                    : null
                }
              />
            </Stack>
            <Stack spacing={0}>
              <Text fontWeight={"medium"}>Max Number Of Uses Per User</Text>
              <Input
                placeholder="Optional"
                type={"number"}
                isDisabled={loading["promocode"]}
                onChange={(e) => {
                  updateDirtyText({
                    "promocode.max_uses_per_user": e.target.value,
                  });
                }}
                value={
                  `promocode.max_uses_per_user` in dirtyText
                    ? dirtyText[`promocode.max_uses_per_user`]
                    : promoCode
                    ? promoCode.max_uses_per_user
                    : null
                }
              />
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={loading["promocode"]}
            onClick={() => {
              updateDirtyText("all");
              setModalType(EditModalType.INACTIVE);
              setEditingPromoCode(null);
            }}
            mr="4"
          >
            Cancel
          </Button>
          <Button
            color="white"
            bg="green.500"
            isLoading={loading["promocode"]}
            onClick={() =>
              promoCode
                ? onUpdatePromoCode(kydEvent.id, editingPromoCode!)
                : onCreatePromoCode(kydEvent.id)
            }
          >
            {promoCode ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const renderAddAffiliates = (kydEvent: KYDAdminEvent) => (
    <Modal
      size={"xl"}
      isOpen={true}
      onClose={() => setModalType(EditModalType.INACTIVE)}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{"Create Affiliates"}</ModalHeader>
        <ModalBody>
          <Stack spacing={4}>
            <Text>You can create up to 100 affiliates per event.</Text>
            <Stack spacing={0}>
              <Text fontWeight={"medium"}>Quantity</Text>
              <HStack>
                <InputGroup w={"150px"} size={"md"}>
                  <NumberInput
                    isDisabled={loading["create_affiliates"]}
                    onChange={(e) => {
                      console.log("Quantity: ", e);
                      updateDirtyText({ "create_affiliates.quantity": e });
                    }}
                    defaultValue={0}
                    min={0}
                    max={100 - kydEvent.affiliates.length}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </InputGroup>
                <Text fontSize={"sm"} color="gray.500">
                  {100 - kydEvent.affiliates.length} remaining
                </Text>
              </HStack>
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={loading["create_affiliates"]}
            onClick={() => {
              updateDirtyText("all");
              setModalType(EditModalType.INACTIVE);
            }}
            mr="4"
          >
            Cancel
          </Button>
          <Button
            isDisabled={
              loading["create_affiliates"] ||
              !dirtyText["create_affiliates.quantity"] ||
              parseInt(dirtyText["create_affiliates.quantity"]) <= 0 ||
              kydEvent.affiliates.length +
                parseInt(dirtyText["create_affiliates.quantity"]) >
                100
            }
            color="white"
            bg="green.500"
            isLoading={loading["create_affiliates"]}
            onClick={() => onCreateAffiliates(kydEvent.id)}
          >
            Create
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const renderEditAffiliate = (
    kydEvent: KYDAdminEvent,
    affiliate: KYDAffiliate,
    mode: "edit" | "delete"
  ) => (
    <Modal
      size={"xl"}
      isOpen={true}
      onClose={() => setModalType(EditModalType.INACTIVE)}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {mode === "edit" ? "Edit Affiliate" : "Delete Affiliate"}
        </ModalHeader>
        <ModalBody>
          <Stack spacing={4}>
            {mode === "edit" ? (
              <Stack spacing={0}>
                <Text fontWeight={"medium"}>Name</Text>
                <Input
                  isDisabled={loading["edit_affiliate"]}
                  onChange={(e) => {
                    updateDirtyText({ "affiliate.name": e.target.value });
                  }}
                  value={
                    `affiliate.name` in dirtyText
                      ? dirtyText[`affiliate.name`]
                      : affiliate.name
                  }
                />
              </Stack>
            ) : null}
            {mode === "delete" ? (
              <HStack>
                <Text>Are you sure you want to delete this affiliate?:</Text>
                <Text fontWeight={"medium"}>{affiliate.name}</Text>
              </HStack>
            ) : null}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={loading["edit_affiliate"]}
            onClick={() => {
              updateDirtyText("all");
              setModalType(EditModalType.INACTIVE);
              setEditingAffiliate(null);
              setDeleteAffiliate(null);
            }}
            mr="4"
          >
            Cancel
          </Button>
          {mode === "edit" ? (
            <Button
              color="white"
              bg="green.500"
              isLoading={loading["edit_affiliate"]}
              onClick={() => onEditAffiliate(kydEvent.id, affiliate, mode)}
            >
              Save
            </Button>
          ) : null}
          {mode === "delete" ? (
            <Button
              color="white"
              bg="red.500"
              isLoading={loading["edit_affiliate"]}
              onClick={() => onEditAffiliate(kydEvent.id, affiliate, mode)}
            >
              Delete
            </Button>
          ) : null}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const pulse = keyframes({
    "0%": {
      backgroundPosition: "0% 50%",
    },
    "50%": {
      backgroundPosition: "100% 50%",
    },
    "100%": {
      backgroundPosition: "0% 50%",
    },
  });

  const onUpdateTimeline = (data: object) => {
    return onUpdateEvent("timeline", JSON.stringify(data));
  };
  const renderEditModal = (ticket_type: AdminTicketType) => {
    if (ticket_type.tier === 0) {
      setModalType(EditModalType.EDIT_TIERED_TICKET_TYPE);
    } else {
      setModalType(EditModalType.TICKET_TYPE);
    }

    setEditingTicketType(ticket_type);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={{ base: "8", lg: "6" }}>
        {modalType === EditModalType.NAME && renderEditNameModal()}
        {modalType === EditModalType.DESCRIPTION &&
          renderEditDescriptionModal()}
        {modalType === EditModalType.VENUE && renderEditVenueModal()}
        {(modalType === EditModalType.TICKET_TYPE ||
          (editingTicketType && !("tier" in editingTicketType))) &&
          renderEditTicketTypeModal(editingTicketType)}
        {modalType === EditModalType.EDIT_TIERED_TICKET_TYPE &&
          editingTicketType &&
          "tier" in editingTicketType && (
            <EditTieredTypeModal
              updateDirtyText={validateAndUpdateDirtyText}
              error={error.code}
              kydEvent={kydEvent!}
              setModalType={setModalType}
              dirtyText={dirtyText}
              tiers={[editingTicketType, ...tiers[editingTicketType.id]]}
              loading={loading}
              onUpdateTicketType={onUpdateTieredTicketType}
              onDeleteTicketType={onDeleteTicketType}
            />
          )}
        {modalType === EditModalType.PROMO_CODE &&
          renderEditPromoCode(kydEvent!, editingPromoCode)}
        {modalType === EditModalType.CREATE_AFFILIATES &&
          renderAddAffiliates(kydEvent!)}
        {editingAffiliate &&
          renderEditAffiliate(kydEvent!, editingAffiliate, "edit")}
        {deleteAffiliate &&
          renderEditAffiliate(kydEvent!, deleteAffiliate, "delete")}

        {loading["init"] && (
          <VStack>
            <Spinner />
            <Text>Loading event...</Text>
          </VStack>
        )}
        {kydEvent && (
          <Stack spacing={10}>
            <Stack direction={["column", "row"]}>
              <Stack
                w="100%"
                direction={{ base: "column", md: "row" }}
                spacing={3}
              >
                <VStack>
                  <Image
                    fit={"contain"}
                    rounded={"lg"}
                    maxH="150px"
                    maxW={"150px"}
                    src={kydEvent.image}
                    fallbackSrc="https://content.kydlabs.com/system/plcaeholderimage.png"
                  />
                  <Button
                    isDisabled={loading.event || loading.blocking}
                    isLoading={loading.event}
                    onClick={() => {
                      if (uploadRef && uploadRef.current) {
                        uploadRef.current.click();
                      }
                    }}
                    aria-label="Upload event image"
                    variant={"outline"}
                    leftIcon={<FaCloudUploadAlt />}
                  >
                    Change Poster
                  </Button>
                  {progress.poster > 0 && (
                    <Progress
                      h="15px"
                      rounded={"lg"}
                      colorScheme={"green"}
                      value={progress.poster}
                    />
                  )}
                  {(!progress.poster || progress.poster === 0) && (
                    <Box h="15px" w="100%" />
                  )}
                  <ReactS3Uploader
                    style={{ display: "none" }}
                    //@ts-ignore
                    inputRef={uploadRef}
                    getSignedUrl={(file, callback) =>
                      actions.ADMIN.createUploadUrl(file, callback)
                    }
                    accept="image/*"
                    onProgress={(p) => {
                      updateProgress("poster", p);
                      console.log("progress: ", p);
                    }}
                    onError={(e) => {
                      updateLoading("event", false);
                      dialog({ text: e });
                    }}
                    preprocess={(f, next) => {
                      updateLoading("event", true);
                      next(f);
                    }}
                    onFinish={(result) => {
                      onUpdateEvent("poster", result.fileKey);
                    }}
                    uploadRequestHeaders={{ "Content-Type": "image/*" }} // this is the default
                    contentDisposition="auto"
                  />
                </VStack>
                <Stack w="100%" spacing={4}>
                  <Stack
                    w="100%"
                    justify={"space-between"}
                    direction={{ base: "column", md: "row" }}
                  >
                    <Stack spacing={0}>
                      <HStack>
                        <Text fontSize={"xl"} fontWeight="bold">
                          {kydEvent.name}
                        </Text>{" "}
                        <IconButton
                          isDisabled={loading["blocking"]}
                          onClick={() => setModalType(EditModalType.NAME)}
                          icon={<FiEdit2 fontSize="1.25rem" />}
                          variant="ghost"
                          aria-label="Edit member"
                        />
                      </HStack>
                      <Text fontSize={"sm"} fontWeight={"medium"}>
                        {kydEvent.venues[0].name}
                      </Text>
                      <Text fontSize={"sm"} fontWeight={"medium"}>
                        <a
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            textDecoration: "underline",
                          }}
                          href={kydEvent.venues[0].link}
                        >
                          {kydEvent.venues[0].address}
                        </a>
                      </Text>
                      <Text
                        fontSize={"xs"}
                        color="gray.400"
                        fontWeight={"medium"}
                      >
                        ID: {kydEvent.venues[0].id}
                      </Text>
                      <Button
                        maxW={"180px"}
                        isDisabled={loading.event || loading.blocking}
                        isLoading={loading.event}
                        onClick={() => setModalType(EditModalType.VENUE)}
                        aria-label="Change venue"
                        variant={"outline"}
                        leftIcon={<FaWarehouse />}
                      >
                        Change Venue
                      </Button>
                      <Stack spacing={0}>
                        <Text fontSize={"sm"}>
                          Event start: {kydEvent.display_start_at}
                        </Text>
                        <Text fontSize={"sm"}>
                          Event end: {kydEvent.display_end_at}
                        </Text>
                        <Text fontSize={"sm"}>
                          Timezone: {kydEvent.timezone}
                        </Text>
                      </Stack>
                    </Stack>

                    <Stack>
                      {kydEvent.preview_url ? (
                        <HStack>
                          <Button
                            variant={"outline"}
                            onClick={() =>
                              window.open(kydEvent.preview_url, "_blank")
                            }
                            rightIcon={<FiExternalLink />}
                          >
                            Preview Event
                          </Button>
                          <IconButton
                            variant={"outline"}
                            onClick={() => {
                              onCopy();
                              showSuccessToast(toast, "Preview Link Copied");
                            }}
                            icon={<FiCopy />}
                            aria-label="Copy preview link"
                          />
                        </HStack>
                      ) : null}
                      {kydEvent.short_link ? (
                        <>
                          <Button
                            variant={"outline"}
                            onClick={() =>
                              window.open(kydEvent.short_link, "_blank")
                            }
                            rightIcon={<FiExternalLink />}
                          >
                            Go To Event
                          </Button>
                          {kydEvent.short_links?.scanner ? (
                            <Button
                              variant={"outline"}
                              onClick={() =>
                                window.open(
                                  kydEvent.short_links?.scanner,
                                  "_blank"
                                )
                              }
                              bg="black"
                              color={"white"}
                              rightIcon={<FiExternalLink />}
                            >
                              Go To Scanner
                            </Button>
                          ) : null}
                        </>
                      ) : null}
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            {kydEvent.status !== "DRAFT" ? (
              <Stack w="100%" align={"flex-end"}>
                <SimpleGrid w="100%" columns={{ base: 1, md: 4 }} gap="6">
                  {renderStat({
                    label: "Gross Ticket Sales",
                    value: kydEvent.total_gmv_display,
                    height: "90px",
                  })}
                  {renderStat({
                    label: "% Tickets Sold",
                    value: kydEvent.total_tickets_purchased_percent_display,
                    height: "90px",
                  })}
                  {renderStat({
                    label: "# Tickets Sold",
                    value: kydEvent.total_tickets_purchased_display,
                    height: "90px",
                  })}
                  {kydEvent.total_attendees > 0
                    ? renderStat({
                        label: "Total Attendees (Scans)",
                        value: kydEvent.total_attendees_display,
                        height: "90px",
                      })
                    : null}
                  {
                    <Box
                      px={{ base: "4", md: "4" }}
                      py={{ base: "5", md: "4" }}
                      bg="bg-surface"
                      borderRadius="lg"
                      borderWidth={"1px"}
                      borderColor="gray.200"
                      height="135px"
                    >
                      <Stack>
                        <Text fontSize="sm" color="muted">
                          {"Scanner Code"}
                        </Text>
                        <Heading size={headerSize}>
                          {kydEvent.access_code}
                        </Heading>
                        <Button
                          isDisabled={loading.event || loading.blocking}
                          isLoading={loading.event}
                          onClick={() => {
                            const timestamp = new Date().toISOString();
                            const formatString = `TEST/${timestamp}`;
                            const checksumSlice = 10;
                            const checksum = md5(formatString).slice(
                              0,
                              checksumSlice
                            );
                            const withChecksum = `${formatString}/${checksum}/${checksumSlice}`;
                            setAppModalType(
                              ModalType.SHOW_WALLET,
                              withChecksum
                            );
                          }}
                          aria-label="Generate test code"
                          variant={"outline"}
                          leftIcon={<FaQrcode />}
                        >
                          Generate Test Code
                        </Button>
                      </Stack>
                    </Box>
                  }
                </SimpleGrid>
              </Stack>
            ) : null}

            {loading.metrics && kydEvent.status !== "DRAFT" ? (
              <VStack w="100%">
                <Spinner />
                <Text>Loading ticket sales...</Text>
              </VStack>
            ) : null}
            {!loading.metrics && metrics && kydEvent.status !== "DRAFT" ? (
              <TicketSales eventMetrics={metrics} />
            ) : null}

            {renderDescription(kydEvent)}
            {renderTicketTypes(kydEvent)}
            <TimelineSection
              loading={loading}
              onSave={(data) => {
                onUpdateTimeline(data);
              }}
              setModalType={setModalType}
              onCancel={() => setModalType(EditModalType.INACTIVE)}
              kydEvent={kydEvent}
              isOpen={modalType === EditModalType.TIMELINE}
            />
            {renderMarketing(kydEvent)}
            {renderCampaignsTable(kydEvent)}
            {renderPromoCodes(kydEvent)}
            {renderAffiliates(kydEvent)}
            {kydEvent.ticket_types.filter((tt) => tt.status === "DRAFT")
              .length > 0 &&
            kydEvent.image &&
            kydEvent.status !== "PENDING_APPROVAL" &&
            kydEvent.description ? (
              <Box
                bg="white"
                borderWidth={"1px"}
                borderColor={"gray.100"}
                roundedTop={"lg"}
                p={4}
                w="100%"
                shadow={"lg"}
                bottom={0}
                position={"sticky"}
              >
                <Button
                  onClick={() => onLaunchEvent(event_id!)}
                  isDisabled={loading["blocking"]}
                  isLoading={loading["blocking"]}
                  background="linear-gradient(-45deg, #FFA63D, #FF3D77, #338AFF, #3CF0C5)"
                  backgroundSize="600%"
                  animation={`${pulse} 4s linear infinite`}
                  color="white"
                  w="100%"
                  h="50px"
                  _hover={{ backgroundColor: "none" }}
                >
                  {`Launch ${
                    kydEvent.status === "OPEN" ? "Changes" : "Event"
                  } 🚀`}
                </Button>
              </Box>
            ) : null}
            {kydEvent.status === "PENDING_APPROVAL" ? (
              <VStack
                bg="yellow.100"
                borderWidth={"1px"}
                borderColor={"gray.100"}
                roundedTop={"lg"}
                p={4}
                w="100%"
                shadow={"lg"}
                bottom={0}
                position={"sticky"}
              >
                <Text fontWeight={"bold"}>🔍 Pending Approval</Text>
              </VStack>
            ) : null}
          </Stack>
        )}
      </Stack>
    </LocalizationProvider>
  );
};

export default App;
